import React from 'react';
import { SafeImage } from './SafeImage';
import { ChevronRight } from 'lucide-react';

interface PageHeaderBannerProps {
  breadcrumb: string;
  parentPage?: { label: string; page: string };
  badgeIcon?: React.ReactNode;
  badgeText: string;
  badgeColor?: 'emerald' | 'sky' | 'amber';
  title: string;
  description: string;
  backgroundImage: string;
  fallbackCategory?: string;
  gradientTheme?: 'emerald-amber' | 'sky-slate' | 'teal-emerald' | 'cyan-indigo';
  onNavigate?: (page: string) => void;
  actions?: React.ReactNode;
}

export const PageHeaderBanner: React.FC<PageHeaderBannerProps> = ({
  breadcrumb,
  parentPage = { label: 'Home', page: 'home' },
  badgeIcon,
  badgeText,
  badgeColor = 'emerald',
  title,
  description,
  backgroundImage,
  fallbackCategory = 'general',
  gradientTheme = 'emerald-amber',
  onNavigate,
  actions
}) => {
  // Gradient overlay matching the uploaded example's rich duotone lighting
  const getGradientClass = () => {
    switch (gradientTheme) {
      case 'emerald-amber':
        // Exact match to uploaded example: deep forest green/emerald on left, dark slate center, warm amber/terracotta on right
        return 'from-emerald-950/92 via-slate-950/86 to-amber-950/82';
      case 'teal-emerald':
        return 'from-teal-950/92 via-slate-950/88 to-emerald-950/80';
      case 'cyan-indigo':
        return 'from-sky-950/92 via-slate-950/88 to-indigo-950/80';
      case 'sky-slate':
      default:
        return 'from-sky-950/90 via-slate-950/85 to-teal-950/80';
    }
  };

  const badgeColorClass =
    badgeColor === 'amber'
      ? 'text-amber-300 border-amber-400/30 bg-black/45'
      : badgeColor === 'sky'
      ? 'text-sky-300 border-sky-400/30 bg-black/45'
      : 'text-emerald-300 border-emerald-400/30 bg-black/45';

  const handleParentClick = (e: React.MouseEvent) => {
    if (onNavigate && parentPage) {
      e.preventDefault();
      onNavigate(parentPage.page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full relative overflow-hidden bg-slate-950 text-white border-b border-slate-800">
      {/* Background Image Layer */}
      <div className="absolute inset-0 z-0">
        <SafeImage
          src={backgroundImage}
          fallbackCategory={fallbackCategory}
          alt={title}
          className="w-full h-full object-cover object-center filter brightness-90 transform scale-102"
        />
        {/* Rich duotone gradient scrim directly matching uploaded screenshot */}
        <div className={`absolute inset-0 bg-gradient-to-r ${getGradientClass()} backdrop-blur-2xs`} />
        {/* Subtle dark radial vignette for text pop */}
        <div className="absolute inset-0 bg-radial from-transparent via-black/20 to-black/60 pointer-events-none" />
      </div>

      {/* Foreground Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="max-w-3xl space-y-4">
          {/* Breadcrumbs Navigation */}
          <nav className="flex items-center gap-1.5 text-xs text-slate-300 font-medium">
            <button
              onClick={handleParentClick}
              className="hover:text-white transition-colors cursor-pointer"
            >
              {parentPage.label}
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-white font-semibold">{breadcrumb}</span>
          </nav>

          {/* Pill Badge Container (Exact match to screenshot) */}
          {badgeText && (
            <div className="pt-0.5">
              <span
                className={`inline-flex items-center gap-2 px-3.5 py-1 rounded-full backdrop-blur-md border text-xs font-medium shadow-sm ${badgeColorClass}`}
              >
                {badgeIcon && <span className="shrink-0">{badgeIcon}</span>}
                <span>{badgeText}</span>
              </span>
            </div>
          )}

          {/* Large Bold Page Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
            {title}
          </h1>

          {/* Subtitle / Descriptive Lead */}
          <p className="text-slate-200 text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed font-normal">
            {description}
          </p>

          {/* Optional Action Buttons */}
          {actions && <div className="pt-2 flex flex-wrap items-center gap-3">{actions}</div>}
        </div>
      </div>
    </div>
  );
};
